import React, { useState, useRef, useMemo, useEffect } from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import moment from 'moment';
import endpoints from '../../define/endpoints';
import ModalDrawer from './ModalDrawer';
import Button, { ButtonSizes, ButtonStyles } from '../Button/Button';
import { useMatchesMinWidth } from '../../hooks/useMatchesMinWidth';
import { RadioList } from './RadioList';
import { BetaTextInput } from '../BetaTextInput';
import { useForm } from 'react-hook-form';
import BetaIcon, { IconColors, IconSizes } from '../Icons/Icon';
import { TRANSLATIONS } from '../../../translations';
import { BREAKPOINTS } from '../../../constants';
import { ModalOrderCard } from './subComponents/ModalOrderCard';
import Typography, {
  TypographyStates,
  TypographyVariants,
  TypographyWeights,
} from '../../ui-components/Typography/Typography';
import 'react-dates/initialize';
import { SingleDatePicker } from 'react-dates';
import Select from 'react-select';
import { CustomRadioOption } from '../Select/SelectCustomOptions';
import useAvailabilities from '../../hooks/useAvailabilities';
import Dialog, { DialogAlignments, DialogTypes } from '../Dialog/Dialog';
import { DATE_FORMATS } from '../../../constants';
import { ModalDrawerResponse } from './ModalDrawerResponse';
import { ModalDrawerExitFlow } from './ModalDrawerExitFlow';
import { Loader } from '../common';
import { calculatePriceDifference } from '../../hooks/useOrderListUtils';

const CANCELLATION_MOTIVATION = 'cancellation_motivation';
const CUSTOM_MOTIVATION = 'custom_motivation';
const MOTIVATION = 'motivation';
const modalTitle = TRANSLATIONS.RESCHEDULE_THE_ACTIVITY;

const ModalBookingCompletedReschedule = ({
  isOpen,
  setIsOpen,
  token,
  order,
  motivation = null,
  customGoBackFunc,
  setResponseModal,
  setRequestDetails,
}) => {
  if (!order || !token) return null;

  const orderId = order.id;
  const orderItems = order?.items || [];
  const productId = order.product.id;
  const listingId = order.listing.id;
  const firstStep = motivation ? 2 : 1;

  const isDeviceSmallerThanMblLandscape = !useMatchesMinWidth(
    BREAKPOINTS.MBL_LANDSCAPE
  );
  const { register, handleSubmit, setValue, watch, reset } = useForm();
  const motivationFormRef = useRef(null);
  const datetimeFormRef = useRef(null);

  const { availabilitiesPerDate, date, setDate, isDayBlocked } =
    useAvailabilities(listingId, productId);
  const [responseModalState, setResponseModalState] = useState({
    isOpen: false,
    isSuccess: false,
    responseContent: '',
    modalTitle: '',
  });
  const actualSetResponseModal = setResponseModal || setResponseModalState;
  const [showModalExit, setShowModalExit] = useState(false);
  const [step, setStep] = useState(firstStep);
  const [focus, setFocus] = useState(false);
  const [formData, setFormData] = useState({});
  const [time, setTime] = useState(null);
  const [showCustomInput, setShowCustomInput] = useState(false);
  const [radioListError, setRadioListError] = useState(null);
  const [motivationError, setMotivationError] = useState(null);
  const [dateError, setDateError] = useState(null);
  const [isLoading, setIsLoading] = useState(null);
  const [price, setPrice] = useState(null);
  const [priceDifference, setPriceDifference] = useState(0);

  const motivationsList = useMemo(
    () => ({
      cancellation_motivation_1:
        TRANSLATIONS.ACTIVITY_WAS_CANCELLED_DUE_TO_THE_WEATHER,
      cancellation_motivation_2: TRANSLATIONS.GUIDE_DID_NOT_SHOW_UP,
      other: TRANSLATIONS.OTHER,
    }),
    []
  );

  useEffect(() => {
    document.body.classList.toggle('noscroll', isOpen);
    return () => document.body.classList.remove('noscroll');
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) {
      setStep(1);
      setShowModalExit(false);
      setDate(null);
      setTime(null);
      reset();
      setRadioListError(null);
      setMotivationError(null);
      setDateError(null);
      setPriceDifference(0);
    }
  }, [isOpen]);

  useEffect(() => {
    if (!!availabilitiesPerDate.length) {
      setTime(availabilitiesPerDate[0]);
      setPrice(availabilitiesPerDate[0].price);
      setPriceDifference(
        calculatePriceDifference(availabilitiesPerDate[0].price, orderItems)
      );
    }
  }, [availabilitiesPerDate]);

  useEffect(() => {
    if (price) {
      setPriceDifference(calculatePriceDifference(price, orderItems));
    }
  }, [price]);

  const goBackFun = () => {
    if (customGoBackFunc && step === 2) {
      customGoBackFunc();
    } else {
      setStep(step - 1);
    }
  };

  const onSubmit = async () => {
    //TODO: improve this part, there are two concat
    let newDate = formData.date + ' ' + formData.time;
    let formattedDateTime = moment(newDate, 'DD/MM/YYYY HH:mm').format(
      'YYYY-MM-DD HH:mm:ss'
    );

    try {
      setIsLoading(true);

      const { data } = await axios.post(
        `${endpoints.core}/order-customer-request/store`,
        {
          order_id: orderId,
          type: 'completed_booking_new_date',
          reason: formData.motivation,
          requested_booking_datetime: formattedDateTime,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      let requestDetails = data && data.order ? data.order : null;
      setRequestDetails(requestDetails);
      actualSetResponseModal({
        isOpen: true,
        isSuccess: true,
        responseContent:
          TRANSLATIONS.YOUR_DATE_CHANGE_REQUEST_HAS_BEEN_SUCCESSFULLY_SUBMITTED,
        modalTitle: modalTitle,
      });
    } catch (error) {
      // console.log(error);
      actualSetResponseModal({
        isOpen: true,
        isSuccess: false,
        responseContent:
          TRANSLATIONS.UNEXPECTED_ERROR_HAS_OCCURRED_PLEASE_RETRY_LATER,
        modalTitle: modalTitle,
      });
    } finally {
      setIsLoading(false);
      setIsOpen(false);
    }
  };

  const handleNextStep = (data, event) => {
    event.preventDefault();

    if (!data.date && data.date === '') {
      setDateError(TRANSLATIONS.SELECTION_REQUIRED);
      return;
    }

    if (!data.motivation && data.motivation === '' && !showCustomInput) {
      setRadioListError(TRANSLATIONS.RADIO_LIST_ERROR);
      return;
    } else if (!data.motivation && data.motivation === '' && showCustomInput) {
      setMotivationError(TRANSLATIONS.MOTIVATION_ERROR);
      return;
    }

    if (motivation) {
      data.motivation = motivation;
    }

    const mergedData = { ...formData, ...data };
    setFormData(mergedData);
    setStep(step + 1);
  };

  const motivationForm = (
    <form ref={motivationFormRef} onSubmit={handleSubmit(handleNextStep)}>
      <div className="pb-2xs">
        <RadioList
          inputName={CANCELLATION_MOTIVATION}
          options={motivationsList}
          defaultValue={null}
          onChange={(value) => {
            setValue(CANCELLATION_MOTIVATION, value);
            if (value === 'other') {
              setShowCustomInput(true);
              setValue(MOTIVATION, '');
            } else {
              setShowCustomInput(false);
              setValue(MOTIVATION, motivationsList[value]);
            }
          }}
          oneColumn={true}
          error={radioListError}
          setError={setRadioListError}
        />
      </div>
      {showCustomInput && (
        <>
          <BetaTextInput
            asTextArea
            className="modal-ord-li-review__review-form__field"
            label={TRANSLATIONS.TELL_US_YOUR_MOTIVATION}
            name={CUSTOM_MOTIVATION}
            setRef={register({ required: false, maxLength: { value: 500 } })}
            defaultValue={''}
            onChange={(event) => {
              // const value = event.target.value.replace(
              //   /[^a-zA-Z0-9\s.,!?'-]/g,
              //   ''
              // );
              const value = event.target.value;
              setValue(MOTIVATION, value);
              setValue(CUSTOM_MOTIVATION, value);
            }}
            resize={true}
            limit={500}
            error={motivationError}
            setError={setMotivationError}
            errorMessage={TRANSLATIONS.MOTIVATION_ERROR}
          />
        </>
      )}
      <input type="hidden" name="motivation" ref={register} />
    </form>
  );

  const datetimeForm = (
    <form ref={datetimeFormRef} onSubmit={handleSubmit(handleNextStep)}>
      <div className="modal-datetime-form d-flex flex-column gap-xs">
        <div
          className={`modal-datetime-form__input ${dateError ? 'modal-datetime-form__input--errored' : ''
            }`}
        >
          <div className="relative">
            <SingleDatePicker
              id="datetime-form-input"
              numberOfMonths={1}
              hideKeyboardShortcutsPanel={true}
              keepOpenOnDateSelect={false}
              displayFormat={'D MMM YYYY'}
              isOutsideRange={() => false}
              date={date}
              placeholder={TRANSLATIONS.SELECT_A_DATE}
              onDateChange={(value) => {
                setDate(value), setDateError(null);
              }}
              focused={focus}
              onFocusChange={({ focused }) => setFocus(focused)}
              isDayBlocked={isDayBlocked}
              readOnly
            />
            <div className={'modal-datetime-form__input-label'}>
              <label>{TRANSLATIONS.EDIT}</label>
            </div>
          </div>
          {dateError && (
            <div
              className={
                'w-100% pt-3xs modal-datetime-form__input-label--errored'
              }
            >
              {dateError}
            </div>
          )}
        </div>
        <div
          className={`modal-datetime-form__input ${dateError ? 'modal-datetime-form__input--errored' : ''
            }`}
        >
          <div className="relative">
            <Select
              classNamePrefix="modal-datetime-form__time-select"
              key={time?.id || 'modal-datetime-form'}
              onMenuOpen={() => setFocus(null)}
              placeholder={TRANSLATIONS.SELECT_TIME}
              inputProps={{ readOnly: true }}
              isSearchable={false}
              isDisabled={!date}
              options={availabilitiesPerDate}
              onChange={(value) => setTime(value)}
              defaultValue={time}
              isModalOpen={true}
              components={{
                IndicatorSeparator: () => null,
                Option: (props) => (
                  <CustomRadioOption {...props} selected={time?.label} />
                ),
                DropdownIndicator: () => null,
              }}
            />
            <div
              className={`modal-datetime-form__input-label ${!date ? 'modal-datetime-form__input-label--is-disabled' : ''
                }`}
            >
              <label>{TRANSLATIONS.EDIT}</label>
            </div>
          </div>
          {dateError && (
            <div
              className={
                'w-100% pt-3xs modal-datetime-form__input-label--errored'
              }
            >
              {dateError}
            </div>
          )}
        </div>

        {priceDifference !== 0 && (
          <Dialog
            align={DialogAlignments.row}
            type={DialogTypes.feature}
            content={
              priceDifference < 0
                ? TRANSLATIONS.SELECTED_DATE_REQUIRES_REFUND.replace(
                  '{{priceDifference}}',
                  Math.abs(priceDifference)
                )
                : TRANSLATIONS.SELECTED_DATE_REQUIRES_EXTRA_CHARGE.replace(
                  '{{priceDifference}}',
                  Math.abs(priceDifference)
                )
            }
            icon={
              <BetaIcon
                name={'info'}
                color={IconColors.regular}
                size={IconSizes[24]}
              />
            }
          />
        )}

        <input
          type="hidden"
          name={'date'}
          ref={register}
          value={(date && date.format(DATE_FORMATS.USER_FORM_FE)) || ''}
        />
        <input
          type="hidden"
          name={'time'}
          ref={register}
          value={(time && time.label) || ''}
        />
      </div>
    </form>
  );

  const footerContent = (
    <>
      <div className="d-flex gap-m pb-m pl-xl pr-xl">
        <Button
          disabled={isLoading}
          classNames={'flex-1 pr-m pl-m'}
          size={ButtonSizes.s}
          style={ButtonStyles.empty}
          label={TRANSLATIONS.CANCEL}
          onClick={() => setShowModalExit(true)}
        />
        <Button
          disabled={isLoading}
          classNames={'flex-1 pr-m pl-m'}
          size={ButtonSizes.s}
          style={ButtonStyles.secondary}
          label={TRANSLATIONS.CONTINUE}
          onClick={() => {
            if (step === 1) {
              motivationFormRef.current?.dispatchEvent(
                new Event('submit', { cancelable: true })
              );
            } else if (step === 2) {
              datetimeFormRef.current?.dispatchEvent(
                new Event('submit', { cancelable: true })
              );
            } else if (step === 3) {
              onSubmit();
            }
          }}
        />
      </div>
    </>
  );

  const resetErrors = () => {
    setTimeout(() => {
      setRadioListError(null);
      setMotivationError(null);
      setDateError(null);
    }, 500);
  };

  const firstStepModal = (
    <ModalDrawer
      isOpen={isOpen && step === 1 && !showModalExit}
      setIsOpen={setIsOpen}
      isDeviceSmallerThanTablet={isDeviceSmallerThanMblLandscape}
      modalCustomWidth={640}
      title={modalTitle}
      content={
        <div className="pl-m pr-m pt-xl pb-xl w-100% d-flex flex-column gap-m">
          <div>
            <Typography
              classNames="text-left"
              variant={TypographyVariants.p2}
              weight={TypographyWeights.bold}
            >
              {TRANSLATIONS.WE_ARE_SORRY_YOUR_ADVENTURE_DID_NOT_GO_AS_PLANNED}
            </Typography>
            <Typography classNames="text-left" variant={TypographyVariants.p3}>
              {
                TRANSLATIONS.HELP_US_BETTER_UNDERSTAND_WHAT_HAPPENED_PLEASE_SELECT_THE_REASON
              }
            </Typography>
          </div>
          {motivationForm}
          <Typography
            classNames="text-left"
            variant={TypographyVariants.p4}
            state={TypographyStates['neutral-light']}
          >
            {
              TRANSLATIONS.WE_APPRECIATE_YOUR_FEEDBACK_EVERY_REPORT_HELPS_US_IMPROVE
            }
          </Typography>
        </div>
      }
      footer={footerContent}
      handleAfterClose={resetErrors}
    />
  );

  const secondStepModal = (
    <ModalDrawer
      fullDrawer={true}
      goBack={goBackFun}
      isOpen={isOpen && step === 2 && !showModalExit}
      setIsOpen={setIsOpen}
      isDeviceSmallerThanTablet={isDeviceSmallerThanMblLandscape}
      modalCustomWidth={640}
      title={modalTitle}
      content={
        <div className="pl-m pr-m pt-xl pb-xl w-100% d-flex flex-column gap-m">
          <Typography
            classNames="text-left"
            variant={TypographyVariants.p2}
            weight={TypographyWeights.bold}
          >
            {TRANSLATIONS.WHEN_WOULD_YOU_LIKE_TO_DO_THE_ACTIVITY}
          </Typography>
          {datetimeForm}
        </div>
      }
      footer={footerContent}
    />
  );

  const thirdStepModal = (
    <ModalDrawer
      fullDrawer={true}
      goBack={goBackFun}
      isOpen={isOpen && step === 3 && !showModalExit}
      setIsOpen={setIsOpen}
      isDeviceSmallerThanTablet={isDeviceSmallerThanMblLandscape}
      modalCustomWidth={640}
      title={modalTitle}
      content={
        <div className="pl-m pr-m pt-xl pb-xl w-100% d-flex flex-column gap-m">
          {isLoading ? (
            <Loader loading={isLoading} />
          ) : (
            <>
              <Dialog
                align={DialogAlignments.row}
                type={DialogTypes.feature}
                content={TRANSLATIONS.HOW_IT_WORKS_THE_DATE_CHANGE}
                icon={
                  <BetaIcon
                    name={'info'}
                    color={IconColors.regular}
                    size={IconSizes[32]}
                  />
                }
              />
              <div className="d-flex flex-column gap-xs">
                <div
                  className="typography__p3 typography__neutral text-left"
                  dangerouslySetInnerHTML={{
                    __html: TRANSLATIONS.DATE_CHANGE_REQUEST_SUMMARY,
                  }}
                />
                <ModalOrderCard
                  order={order}
                  editRequest={() => setStep(2)}
                  toReschedule={true}
                  motivation={formData.motivation}
                  newDate={formData.date + ' - ' + formData.time}
                  priceDifference={priceDifference}
                />
              </div>
            </>
          )}
        </div>
      }
      footer={footerContent}
    />
  );

  const responseModalDrawer = (
    <ModalDrawerResponse
      isOpen={responseModalState.isOpen}
      setIsOpen={actualSetResponseModal}
      isSuccess={responseModalState.isSuccess}
      modalTitle={responseModalState.modalTitle}
      responseContent={responseModalState.responseContent}
      isDeviceSmallerThanTablet={isDeviceSmallerThanMblLandscape}
    />
  );

  return (
    <>
      {responseModalDrawer}
      {firstStepModal}
      {secondStepModal}
      {thirdStepModal}
      <ModalDrawerExitFlow
        isOpen={showModalExit}
        setIsOpen={setShowModalExit}
        closeAction={() => setIsOpen(false)}
        isDeviceSmallerThanTablet={isDeviceSmallerThanMblLandscape}
      />
    </>
  );
};

ModalBookingCompletedReschedule.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  setIsOpen: PropTypes.func.isRequired,
  token: PropTypes.string.isRequired,
  order: PropTypes.object.isRequired,
  motivation: PropTypes.string,
  customGoBackFunc: PropTypes.func,
  setResponseModal: PropTypes.func,
};

export { ModalBookingCompletedReschedule };
